import { useCallback, useEffect, useRef, useState } from 'react';
import {
  githubAuthApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import type { SignInPageProps } from '@backstage/core-plugin-api';
import { UserIdentity } from '@backstage/core-components';
import { useSearchParams } from 'react-router-dom';
import { LandingPage } from './LandingPage';

/** Presentation adapter for Backstage's existing GitHub auth and session APIs. */
export function GitHubSignIn({ onSignInSuccess }: SignInPageProps) {
  const authApi = useApi(githubAuthApiRef);
  const analytics = useAnalytics();
  const [searchParams] = useSearchParams();
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string>();
  const inFlight = useRef(false);
  const mounted = useRef(false);
  const login = useCallback(
    async (existing: boolean) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      setError(undefined);
      try {
        // Backstage owns the OAuth popup, callback, tokens, and session refresh.
        const response = await authApi.getBackstageIdentity(
          existing ? { optional: true } : { instantPopup: true },
        );
        if (!response) {
          if (!existing) throw new Error('No Backstage identity returned');
          return;
        }
        const profile = await authApi.getProfile();
        if (mounted.current) {
          onSignInSuccess(
            UserIdentity.create({
              identity: response.identity,
              authApi,
              profile,
            }),
          );
          analytics.captureEvent('signIn', 'success');
        }
      } catch (cause) {
        if (
          mounted.current &&
          !(cause instanceof Error && cause.name === 'PopupRejectedError')
        ) {
          // Do not render provider errors that may contain URLs or auth parameters.
          setError(
            'Unable to sign in. Try again with your GitHub account that has active Snap-eHealth organization membership.',
          );
        }
      } finally {
        inFlight.current = false;
        if (mounted.current) setBusy(false);
      }
    },
    [authApi, analytics, onSignInSuccess],
  );

  useEffect(() => {
    mounted.current = true;
    void login(true);
    return () => {
      mounted.current = false;
    };
  }, [login]);

  const redirectError = searchParams.has('error')
    ? 'GitHub sign-in could not be completed. Please try again.'
    : undefined;

  return (
    <LandingPage
      onEnter={() => {
        void login(false);
      }}
      busy={busy}
      error={error ?? redirectError}
    />
  );
}
